'use client';

import type { CSSProperties, ReactElement } from 'react';
import { cloneElement } from 'react';
import type { INavListItem, INavListItemContentListItem } from '@/api/types/public';
import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { ProviderType } from '@/reporting';
import { useSpmDomAttr, withSpmCnt } from '@/reporting/spm';
import { setSession } from '@/utils/tools';
import { setCampaignEntrancePageScene } from '@/utils/tracking/entrancePageScene';
import { EPlatform } from '@/utils/enums/platform-enum';
import { getSpmPositionCode, useGlobalSpmTerminal } from '@/components/Layout/spmTerminal';
import styles from './index.module.scss';
import { getImageNavigationPreferredWidth } from './navigationLayoutPriority';
import { buildSubmenuSpmPosition } from './submenuSpm';

interface ImageNavigationNavProps {
  item: INavListItem;
  firstLevelIndex: number;
  firstLevelTitle: string;
  isDarkMode: boolean;
  /** 扁平坑位序号偏移：本图片分类模块前所有区块的条目累计数；用于重建 submenu_item_GG_II 的 II。 */
  spmItemOffset?: number;
}

const getShopSku = (sku?: INavListItemContentListItem['sku']) =>
  Array.isArray(sku) ? sku[0] : sku?.shop_sku;

/**
 * 循环内挂载点 Data SPM DOM 打标：hook 不能在 map 中调用，封为小组件把 data-spm
 * 克隆到唯一真实子元素上（不新增 DOM 层级）；开关关闭或编码表外时原样渲染，零 DOM 差异。
 */
function SpmDomNode({ code, children }: { code: string | undefined; children: ReactElement }) {
  const domSpm = useSpmDomAttr(code);
  return domSpm ? cloneElement(children, domSpm) : children;
}

const ImageNavigationNav = ({
  item,
  firstLevelIndex,
  firstLevelTitle,
  isDarkMode,
  spmItemOffset = 0,
}: ImageNavigationNavProps) => {
  const { trackEvent } = useReporting();
  const { cnName } = useRouteMetadata();
  // 图片分类区块只在 PC 下拉面板渲染；全局组件 B 位固定 global_pc，表外坑位不附加。
  const spmPage = useGlobalSpmTerminal(EPlatform.WEB);
  const content = item.content;
  const imageItems = content?.list?.filter((listItem) => Boolean(listItem.jump_image)) || [];
  const imageGridMaxWidth = getImageNavigationPreferredWidth(imageItems.length);

  if (imageItems.length === 0) return null;

  const handleBeforeJump = (listItem: INavListItemContentListItem, imageIndex: number) => {
    setSession('zl_goodsListEnterPageScene', '全局导航');
    setCampaignEntrancePageScene('顶部导航', listItem.Jump_target || undefined);
    // 图片分类条目接在文字分类之后扁平编号（submenu_item_GG_II）；表外条目不附加。
    const submenuSpmCnt = getSpmPositionCode(
      spmPage,
      'primary_navigation',
      buildSubmenuSpmPosition(firstLevelIndex, spmItemOffset + imageIndex),
    );
    const trackProperties = {
      page_name: cnName,
      url_path: window.location.href,
      tab_name: firstLevelTitle,
      tab_index: firstLevelIndex,
      pits_index: imageIndex,
      pits_title: listItem.title || '',
    };
    trackEvent(
      {
        eventName: 'TopNavigationMkt_Click',
        properties: submenuSpmCnt ? withSpmCnt(trackProperties, submenuSpmCnt) : trackProperties,
      },
      [ProviderType.SENSORS],
    );
  };

  return (
    <section
      className={`${styles.imageNavigationModule} ${isDarkMode ? 'text-white' : 'text-[#282828]'}`}
      aria-label={content?.layout_title || undefined}
    >
      {content?.layout_title ? (
        <b className="block font-bb text-[16px] leading-[19px] [font-weight:inherit]">
          {content.layout_title}
        </b>
      ) : null}

      <div
        className={styles.imageNavigationGrid}
        style={
          {
            '--image-navigation-grid-max-width': `${imageGridMaxWidth}px`,
          } as CSSProperties
        }
      >
        {imageItems.map((listItem, imageIndex) => {
          const title = listItem.title || '';
          const jumpTarget = {
            jump_type: listItem.jump_type ?? 0,
            Jump_target: listItem.Jump_target || '',
            product_group: {
              id: listItem.product_group?.id ?? undefined,
              path: listItem.product_group?.path,
            },
            sku: {
              shop_sku: getShopSku(listItem.sku) || '',
            },
          };

          return (
            <SpmDomNode
              key={listItem.id ?? `${title}-${imageIndex}`}
              code={getSpmPositionCode(
                spmPage,
                'primary_navigation',
                buildSubmenuSpmPosition(firstLevelIndex, spmItemOffset + imageIndex),
              )}
            >
              <MagicJump
                className={`${styles.imageNavigationCard} group block overflow-hidden`}
                item={jumpTarget}
                onBeforeJump={() => handleBeforeJump(listItem, imageIndex)}
              >
                <div className="relative aspect-[196/245] w-full overflow-hidden">
                  <Image
                    src={listItem.jump_image || ''}
                    alt={title}
                    fill
                    sizes="(max-width: 1366px) 11vw, 196px"
                    className="object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                </div>
                {title ? (
                  <div className="truncate py-[8px] text-center text-[14px] leading-[17px]">
                    {title}
                  </div>
                ) : null}
              </MagicJump>
            </SpmDomNode>
          );
        })}
      </div>
    </section>
  );
};

export default ImageNavigationNav;
